import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './DatePicker.css';

interface DatePickerProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    isLastroDate?: boolean;
    maxDate?: Date;
}

const DAYS_OF_WEEK = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    placeholder = 'dd/mm/aaaa',
    disabled = false,
    label,
    isLastroDate = false,
    maxDate,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [viewDate, setViewDate] = useState(() => {
        if (value) {
            const [year, month] = value.split('-').map(Number);
            return new Date(year, month - 1, 1);
        }
        return new Date();
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxSelectable = maxDate ? new Date(maxDate) : new Date(today);
    maxSelectable.setHours(0, 0, 0, 0);

    const updateDropdownPosition = useCallback(() => {
        if (inputContainerRef.current) {
            const rect = inputContainerRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            const dropdownWidth = rect.width;
            const viewportWidth = window.innerWidth;
            let leftPosition = rect.left;
            if (leftPosition + dropdownWidth > viewportWidth) {
                leftPosition = Math.max(16, viewportWidth - dropdownWidth - 16);
            }

            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: leftPosition,
                width: rect.width,
                maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'none',
                zIndex: 2000,
            });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };

        const handleResize = () => {
            if (isOpen) {
                updateDropdownPosition();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updateDropdownPosition]);

    useEffect(() => {
        if (value) {
            const [year, month] = value.split('-').map(Number);
            setViewDate(new Date(year, month - 1, 1));
        }
    }, [value]);

    const toggleCalendar = () => {
        if (!disabled) {
            if (!isOpen) updateDropdownPosition();
            setIsOpen(prev => !prev);
        }
    };

    const formatDisplay = (val: string) => {
        if (!val) return '';
        const [year, month, day] = val.split('-');
        return `${day}/${month}/${year}`;
    };

    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const prevMonth = () => {
        setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
        if (newDate <= maxSelectable) {
            setViewDate(newDate);
        }
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMonth = parseInt(e.target.value);
        setViewDate(new Date(viewDate.getFullYear(), newMonth, 1));
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newYear = parseInt(e.target.value);
        setViewDate(new Date(newYear, viewDate.getMonth(), 1));
    };

    const getYearOptions = () => {
        const currentYear = today.getFullYear();
        const years = [];
        for (let year = currentYear; year >= currentYear - 100; year--) {
            years.push(year);
        }
        return years;
    };

    const selectDate = (day: number) => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const selectedDate = new Date(year, month, day);

        if (selectedDate > maxSelectable) {
            return;
        }

        const formatted = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        onChange(formatted);
        setIsOpen(false);
    };

    const selectToday = () => {
        const t = new Date();
        const formatted = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        onChange(formatted);
        setIsOpen(false);
    };

    const clearDate = () => {
        onChange('');
        setIsOpen(false);
    };

    const renderCalendar = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const prevMonthDays = getDaysInMonth(year, month - 1);

        const cells: React.ReactNode[] = [];

        for (let i = firstDay - 1; i >= 0; i--) {
            cells.push(
                <div key={`prev-${i}`} className="calendar-day other-month">
                    {prevMonthDays - i}
                </div>
            );
        }

        const selectedParts = value ? value.split('-').map(Number) : null;
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDate = new Date(year, month, day);
            const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
            const isSelected = selectedParts && selectedParts[0] === year && selectedParts[1] === month + 1 && selectedParts[2] === day;
            const isFuture = currentDate > maxSelectable;

            cells.push(
                <div
                    key={`day-${day}`}
                    className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isFuture ? 'disabled' : ''}`}
                    onClick={() => !isFuture && selectDate(day)}
                >
                    {day}
                </div>
            );
        }

        const remaining = 42 - cells.length;
        for (let i = 1; i <= remaining; i++) {
            cells.push(
                <div key={`next-${i}`} className="calendar-day other-month">
                    {i}
                </div>
            );
        }

        return cells;

    };

    const calendarDropdown = isOpen ? (
        <div className="calendar-dropdown" style={dropdownStyle} ref={dropdownRef}>

            <div className="calendar-header">
                <div className="calendar-selects">
                    <select
                        className="calendar-month-select"
                        value={viewDate.getMonth()}
                        onChange={handleMonthChange}
                    >
                        {MONTHS.map((month, index) => {
                            const monthDate = new Date(viewDate.getFullYear(), index, 1);
                            const isDisabled = monthDate > maxSelectable;
                            return (
                                <option key={index} value={index} disabled={isDisabled}>
                                    {month}
                                </option>
                            );
                        })}
                    </select>
                    <select
                        className="calendar-year-select"
                        value={viewDate.getFullYear()}
                        onChange={handleYearChange}
                    >
                        {getYearOptions().map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="calendar-nav">
                    <button className="calendar-nav-btn" onClick={prevMonth} type="button">
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        className="calendar-nav-btn"
                        onClick={nextMonth}
                        type="button"
                        disabled={new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1) > maxSelectable}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
            <div className="calendar-weekdays">
                {DAYS_OF_WEEK.map((d, i) => (
                    <div key={i} className="calendar-weekday">{d}</div>
                ))}
            </div>
            <div className="calendar-grid">
                {renderCalendar()}
            </div>
            <div className="calendar-footer">
                {!isLastroDate && (
                    <button
                        className="calendar-footer-btn"
                        onClick={clearDate} type="button">Limpar</button>
                )}
                <button className="calendar-footer-btn" onClick={selectToday} type="button">Hoje</button>
            </div>
        </div>
    ) : null;

    return (
        <div className="date-picker-container">
            {label && <label className="date-picker-label">{label}</label>}
            <div className="date-picker-wrapper" ref={wrapperRef}>
                <div className="date-picker-input-container" ref={inputContainerRef} onClick={toggleCalendar}>
                    <input
                        type="text"
                        className="date-picker-input"
                        value={formatDisplay(value)}
                        readOnly
                        placeholder={placeholder}
                        disabled={disabled}
                    />
                    <FontAwesomeIcon
                        icon={faCalendarDays}
                        className={`date-picker-icon ${disabled ? 'disabled' : ''}`}
                    />
                </div>
                {calendarDropdown && ReactDOM.createPortal(calendarDropdown, document.body)}
            </div>
        </div>
    );
};

export default DatePicker;
